import { PlusIcon } from '@heroicons/react/24/solid';
import { type ChangeEvent, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { twMerge } from 'tailwind-merge';
import Button from '@/components/UI/Button';
import LoadingTable from '@/components/UI/loading/LoadingTable';
// Custom UI
import Modal from '@/components/UI/Modal';
import { deliverablesTableHeader } from '@/constants/Rover/Applications';
import { useApplicationTeamMembers } from '@/hooks/ReactQueryHooks/useApplicationQuery';
import { useSherpaVendors } from '@/hooks/ReactQueryHooks/useVendorQuery';
import { isObjectEmpty } from '@/utils';

import type { GetDeliverablesResData } from '../../../../../../api/Sherpa/Tdm/types';
// Custom Hooks
import { useRoverDeliverables } from '../../../../../../hooks/ReactQueryHooks/useDeliverablesQuery';
import NewDeliverableForm from '../../NewDeliverableForm';
import DueDiligenceTableFooter from '../DueDiligenceTableFooter';
import DueDiligenceDeliverableRow from './DueDiligenceDeliverableRow';

export default function DueDiligenceDeliverablesTable({ phase }: { phase: { label: string; value: string } }) {
  const { applicationId } = useParams();

  const [fetchParams, setFetchParams] = useState({
    page: 1,
    perPage: 10,
    application_id: applicationId,
    phase: phase.value,
    orderBy: 'sortIndex',
    order: 'asc',
  });

  const [showModal, setShowModal] = useState<{ [key: string]: boolean }>({
    deliverable: false,
    newDeliverable: false,
  });

  useEffect(() => {
    if (phase) {
      setFetchParams((prevState) => {
        return { ...prevState, phase: phase.value };
      });
    }
  }, [phase]);

  const getApplicationTeamMembers = useApplicationTeamMembers({
    applicationId: applicationId!,
  });

  const applicationTeamMembers = getApplicationTeamMembers?.data?.data;

  const teams = [];

  if (applicationTeamMembers?.applicationLead) {
    teams.push(applicationTeamMembers.applicationLead);
  }

  if (applicationTeamMembers?.members) {
    teams.push(...applicationTeamMembers.members.map((member: any) => member?.user));
  }

  const fetchDeliverables = useRoverDeliverables({
    params: { ...fetchParams },
  });

  const deliverables = fetchDeliverables.data?.data;

  const totalPage = Math.ceil(fetchDeliverables.data?.meta.total / fetchParams.perPage);

  const onPageChangeHandler = (direction: string) => {
    if (direction === 'prev') {
      setFetchParams((prevState) => ({
        ...prevState,
        page: prevState.page > 1 ? prevState.page - 1 : 1,
      }));
    } else if (direction === 'next') {
      setFetchParams((prevState) => ({
        ...prevState,
        page: prevState.page === totalPage ? totalPage : prevState.page + 1,
      }));
    }
  };

  const perPageHandler = (e: ChangeEvent<HTMLSelectElement>) => {
    setFetchParams((prevState) => ({
      ...prevState,
      page: 1,
      perPage: Number(e.target.value),
    }));
  };

  const modalHandler = (modalName: string) => {
    setShowModal((prevState) => {
      return {
        ...prevState,
        [modalName]: !prevState[modalName],
      };
    });
  };

  const createNewTdmHandler = () => {
    modalHandler('newDeliverable');
  };

  const tableHeader = [...deliverablesTableHeader];

  const fetchVendors = useSherpaVendors({});
  const vendors = fetchVendors?.data?.data;

  return (
    <div id='deliverables-table' className='rounded-md bg-white w-full'>
      {showModal.newDeliverable && (
        <Modal customStyle='max-w-full'>
          <NewDeliverableForm phase={phase} teams={teams} formModalHandler={modalHandler} />
        </Modal>
      )}
      <div className='flex justify-between items-center border-b-2 border-gray-200 py-3'>
        <h3 className='font-bold text-bold border-l-4 py-4 px-4 border-green-500 capitalize'>Deliverables</h3>
        {applicationTeamMembers && !isObjectEmpty(applicationTeamMembers?.applicationLead) && (
          <div className='px-3'>
            <Button label='Add' btnStyle='btn-primary' btnSize='md' icon={<PlusIcon />} onClick={createNewTdmHandler} />
          </div>
        )}
      </div>

      {fetchDeliverables.isLoading && <LoadingTable />}

      {!fetchDeliverables.isLoading && (
        <div className='w-full overflow-x-auto'>
          <table className='w-full'>
            <thead>
              <tr className='flex justify-between px-5 py-4 bg-gray-200'>
                {tableHeader.map((head) => {
                  return (
                    <th
                      key={head.name}
                      className={twMerge(['flex gap-2 text-neutralColor-normal text-left text-xs uppercase', head.customClassName])}
                    >
                      {head.name}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {fetchDeliverables.isSuccess &&
                deliverables.map((deliverable: GetDeliverablesResData) => (
                  <DueDiligenceDeliverableRow key={deliverable.id} deliverable={deliverable} vendors={vendors} />
                ))}
            </tbody>
          </table>
        </div>
      )}
      <DueDiligenceTableFooter
        pageSize={totalPage > 0 ? totalPage : 1}
        page={fetchParams.page}
        perPage={fetchParams.perPage}
        perPageHandler={perPageHandler}
        pageChangeHandler={onPageChangeHandler}
      />
    </div>
  );
}
